<script setup lang="ts">
import { ArrowLeft, MessageCircle } from '@lucide/vue'
import { computed, ref } from 'vue'

import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import MarkdownDisplay from '@/components/ui/textarea/MarkdownDisplay.vue'


import { useCommunicationFormatters } from '../../composables/useCommunicationFormatters'
import { useCommunicationTranslations } from '../../composables/useCommunicationTranslations'
import type { CommunicationAttachment, CommunicationItem, CommunicationSender } from '../../types/communication'

import AnnouncementAckForm from './AnnouncementAckForm.vue'
import AnnouncementAttachments from './AnnouncementAttachments.vue'
import CommunicationHeader from './CommunicationHeader.vue'

const attachmentsRef = ref<InstanceType<typeof AnnouncementAttachments> | null>(null)

const allFilesViewed = computed(() => attachmentsRef.value?.allFilesViewed ?? true)

const props = defineProps<{
  item: CommunicationItem | null
  /** Hides back button (for dialog/gate use). When true, message, attachments, and ack scroll together below the header. */
  embedded?: boolean
  /** Hides conversation actions (for gate/non-recipient use) */
  hideConversation?: boolean
  /** Responder view: override display data */
  isResponder?: boolean
  communication?: {
    id: string
    title: string
    content: string
    sender: CommunicationSender | null
    sent_at: string | null
    attachments: CommunicationAttachment[]
  }
  conversations?: {
    id: string
    recipient_name: string
    last_message: string | null
    last_message_at: string | null
  }[]
}>()

const emit = defineEmits<{
  acknowledged: [communicationId: string]
  back: []
  'open-conversation': [conversationId?: string]
}>()

const { formatFullDate } = useCommunicationFormatters()
const { tm } = useCommunicationTranslations('communication')

function handleAcknowledged() {
  const commId = props.item?.communication_id ?? props.communication?.id
  if (commId) {
    emit('acknowledged', commId)
  }
}

/**
 * Whether the "Start/View conversation" button should render.
 *
 * Hidden when: embedded view hides it, the viewer is the responder,
 * no item is loaded, OR the communication has no responder targeting (no one can reply).
 */
const canStartConversation = computed(() => {
  if (props.hideConversation || props.isResponder || !props.item) {
    return false
  }
  return props.item.has_conversation || props.item.can_start_conversation
})
</script>

<template>
  <div
    v-if="item || communication"
    :class="[
      'flex min-h-0 w-full min-w-0 flex-col',
      embedded && 'min-h-0 flex-1 overflow-hidden',
    ]"
  >
    <!-- Back button (non-embedded only) -->
    <div v-if="!embedded" class="flex shrink-0 items-center gap-2 pb-4">
      <Button variant="ghost" size="sm" @click="emit('back')">
        <ArrowLeft class="mr-1 size-4" />
        {{ tm('inbox.title') }}
      </Button>
    </div>

    <!-- Header (fixed; does not scroll with message) -->
    <CommunicationHeader :item="item" :communication="communication" />

    <Separator class="my-5 shrink-0" />

    <!-- Embedded: single scroll region (message + attachments + ack + actions) so long attachment lists are not clipped -->
    <div
      v-if="embedded"
      class="min-h-0 w-full min-w-0 flex-1 overflow-y-auto overflow-x-hidden"
    >
      <div class="space-y-6 pb-2 pr-1">
        <div class="min-w-0 max-w-full break-words [&_.ProseMirror]:min-w-0 [&_.ProseMirror]:break-words [&_.ProseMirror]:max-w-full">
          <MarkdownDisplay :content="item?.content || communication?.content" />
        </div>

        <AnnouncementAttachments
          ref="attachmentsRef"
          :attachments="item?.attachments || communication?.attachments || []"
          :communication-id="item?.communication_id || communication?.id || ''"
          :requires-signature="item?.requires_signature || false"
        />

        <div v-if="item?.is_pending_acknowledgment">
          <AnnouncementAckForm
            :communication-id="item.communication_id"
            :requires-signature="item.requires_signature"
            :all-files-viewed="allFilesViewed"
            @acknowledged="handleAcknowledged"
          />
        </div>

        <template v-if="canStartConversation && item">
          <Separator />
          <Button
            variant="secondary"
            @click="emit('open-conversation', item.conversation_id || undefined)"
          >
            <MessageCircle class="mr-2 size-4" />
            {{ item.has_conversation ? tm('detail.view_conversation') : tm('detail.start_conversation') }}
          </Button>
        </template>

        <template v-if="isResponder && conversations && conversations.length > 0">
          <Separator />
          <h2 class="text-base font-semibold">{{ tm('detail.conversations') }}</h2>
          <p class="mt-1 text-sm text-muted-foreground">{{ tm('detail.select_conversation') }}</p>

          <div class="divide-y rounded-lg border">
            <div
              v-for="conv in conversations"
              :key="conv.id"
              class="flex cursor-pointer items-center justify-between p-4 transition-colors hover:bg-muted/50"
              @click="emit('open-conversation', conv.id)"
            >
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="font-medium">{{ conv.recipient_name }}</span>
                  <span v-if="conv.last_message_at" class="text-xs text-muted-foreground">
                    {{ formatFullDate(conv.last_message_at) }}
                  </span>
                </div>
                <p class="mt-0.5 truncate text-sm text-muted-foreground">
                  {{ conv.last_message || tm('messages.no_messages_yet') }}
                </p>
              </div>
              <ArrowLeft class="ml-4 size-4 rotate-180 text-muted-foreground" />
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Non-embedded: message scrolls independently; attachments stay below -->
    <template v-else>
      <div class="max-h-[60vh] min-h-0 w-full overflow-y-auto overflow-x-hidden">
        <div class="min-w-0 max-w-full break-words [&_.ProseMirror]:min-w-0 [&_.ProseMirror]:break-words">
          <MarkdownDisplay :content="item?.content || communication?.content" />
        </div>
      </div>

      <div class="mt-6 shrink-0">
        <AnnouncementAttachments
          ref="attachmentsRef"
          :attachments="item?.attachments || communication?.attachments || []"
          :communication-id="item?.communication_id || communication?.id || ''"
          :requires-signature="item?.requires_signature || false"
        />
      </div>

      <div v-if="item?.is_pending_acknowledgment" class="mt-6 shrink-0">
        <AnnouncementAckForm
          :communication-id="item.communication_id"
          :requires-signature="item.requires_signature"
          @acknowledged="handleAcknowledged"
        />
      </div>

      <template v-if="canStartConversation && item">
        <Separator class="my-6 shrink-0" />
        <div class="shrink-0">
          <Button
            variant="secondary"
            @click="emit('open-conversation', item.conversation_id || undefined)"
          >
            <MessageCircle class="mr-2 size-4" />
            {{ item.has_conversation ? tm('detail.view_conversation') : tm('detail.start_conversation') }}
          </Button>
        </div>
      </template>

      <template v-if="isResponder && conversations && conversations.length > 0">
        <Separator class="my-6 shrink-0" />
        <div class="shrink-0">
          <h2 class="text-base font-semibold">{{ tm('detail.conversations') }}</h2>
          <p class="mt-1 text-sm text-muted-foreground">{{ tm('detail.select_conversation') }}</p>

          <div class="mt-3 divide-y rounded-lg border">
            <div
              v-for="conv in conversations"
              :key="conv.id"
              class="flex cursor-pointer items-center justify-between p-4 transition-colors hover:bg-muted/50"
              @click="emit('open-conversation', conv.id)"
            >
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="font-medium">{{ conv.recipient_name }}</span>
                  <span v-if="conv.last_message_at" class="text-xs text-muted-foreground">
                    {{ formatFullDate(conv.last_message_at) }}
                  </span>
                </div>
                <p class="mt-0.5 truncate text-sm text-muted-foreground">
                  {{ conv.last_message || tm('messages.no_messages_yet') }}
                </p>
              </div>
              <ArrowLeft class="ml-4 size-4 rotate-180 text-muted-foreground" />
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>

  <!-- Loading state -->
  <div v-else class="flex h-40 items-center justify-center">
    <p class="animate-pulse text-sm text-muted-foreground">{{ tm('messages.loading_messages') }}</p>
  </div>
</template>
